import { useEffect, useRef } from "react";
import { LogOut } from "lucide-react";
import type { ViewDefinition, ViewId } from "../lib/views.js";
import { isPlainLeftClick } from "../lib/useRoute.js";
import { cn } from "../lib/cn.js";

interface Props {
  email: string;
  views: readonly ViewDefinition[];
  current: ViewId;
  onNavigate: (path: string) => void;
  onSignOut: () => void;
  /** Owned by the shell, so only one header dropdown is ever open. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * The design's avatar, and the menu the design implied but never built.
 *
 * In the design the avatar is a `<div onClick>` that opens the settings
 * dialog, captioned with a hardcoded name and a photograph loaded from
 * `/profile_avatar.jpg`. None of that survives contact with a real account:
 * there is no photograph to load, the caption would be a fabricated identity,
 * and a clickable `div` has no keyboard path at all.
 *
 * So the avatar keeps its exact place, size and online badge, draws the
 * signed-in address's initial instead of a photograph, and opens a real menu.
 * The menu is where the five links that belong to the person rather than the
 * installation live -- Site, Telemetry, Settings, Sessions, Activity -- along
 * with the address itself and the sign-out control. That is what keeps the
 * design's five-pill rail at five pills.
 */
export function AccountMenu({
  email,
  views,
  current,
  onNavigate,
  onSignOut,
  open,
  onOpenChange,
}: Props) {
  const root = useRef<HTMLDivElement>(null);

  // Escape closes, and a click anywhere else closes. Without the second one
  // the menu stays open behind whatever the reader went on to press, which is
  // how a menu ends up covering the page it just navigated to.
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onOpenChange(false);
    };
    const onPointer = (event: MouseEvent) => {
      if (!(event.target instanceof Node)) return;
      if (root.current?.contains(event.target) === true) return;
      onOpenChange(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointer);
    };
  }, [open, onOpenChange]);

  const initial = (email.trim()[0] ?? "?").toUpperCase();

  return (
    <div ref={root} className="relative ml-1">
      <button
        type="button"
        onClick={() => {
          onOpenChange(!open);
        }}
        aria-expanded={open}
        aria-haspopup="menu"
        className="group relative block rounded-full outline-none"
      >
        <span className="sr-only">Account</span>
        <span
          aria-hidden="true"
          className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-transparent bg-line text-sm font-semibold text-ink shadow-sm transition-colors group-hover:border-accent sm:h-10 sm:w-10"
        >
          {initial}
        </span>
        {/* The design's online dot, kept. It says the browser holds a session,
            which is the one thing about the account this pixel can honestly
            report -- it is not a claim about the inverter, and the freshness
            line on the page is what carries that. */}
        <span
          aria-hidden="true"
          className="absolute bottom-0 right-0 h-3 w-3 rounded-full bg-accent-2 ring-2 ring-page"
        />
      </button>

      {open && (
        <div className="absolute right-0 top-full z-40 mt-2 w-64 overflow-hidden rounded-card border border-line bg-card shadow-2xl">
          {/* Rendered exactly once in the whole shell. The browser suite
              asserts `getByText(email)` is visible and that locator is strict:
              a second copy shown at another breakpoint matches two elements
              and fails. */}
          <p className="truncate border-b border-line px-4 py-3 text-xs text-ink-muted">{email}</p>

          {views.length > 0 && (
            <ul className="p-1.5">
              {views.map((view) => (
                <li key={view.id}>
                  <a
                    href={view.path}
                    aria-current={view.id === current ? "page" : undefined}
                    onClick={(event) => {
                      if (!isPlainLeftClick(event)) return;
                      event.preventDefault();
                      onOpenChange(false);
                      onNavigate(view.path);
                    }}
                    className={cn(
                      "block rounded-xl px-3 py-2 text-sm font-medium transition-colors",
                      view.id === current
                        ? "bg-btn text-btn-ink"
                        : "text-ink-muted hover:bg-line hover:text-ink",
                    )}
                  >
                    {view.label}
                  </a>
                </li>
              ))}
            </ul>
          )}

          <div className="border-t border-line p-1.5">
            <button
              type="button"
              onClick={() => {
                onOpenChange(false);
                onSignOut();
              }}
              className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm font-medium text-ink transition-colors hover:bg-line"
            >
              <LogOut size={16} aria-hidden="true" />
              {/* Named exactly "Sign out". The Sessions view has its own
                  per-row "Sign out" buttons and names the current device's
                  "Sign out here", which is how a test tells them apart -- so
                  this one must not be renamed to something friendlier. */}
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
